import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CatalogPage } from '@/pages/catalogPage';
import { HealthPage } from '@/pages/healthPage';
import { LogsPage } from '@/pages/logsPage';
import { PerformancePage } from '@/pages/performancePage';
import { QueryPage } from '@/pages/queryPage';
import { UsagePage } from '@/pages/usagePage';

const routeView = (search: string): string | null => new URLSearchParams(search).get('view');

const WorkspaceTabs = ({
  children,
  defaultView,
  options,
  pathname,
}: {
  children: ReactNode;
  defaultView: string;
  options: { label: string; value: string }[];
  pathname: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedView = routeView(location.search);
  const view = options.some((option) => option.value === requestedView) ? (requestedView ?? defaultView) : defaultView;

  return (
    <div className="grid min-w-0 gap-5">
      <Tabs
        onValueChange={(nextView) => {
          const search = new URLSearchParams(location.search);
          if (nextView === defaultView) search.delete('view');
          else search.set('view', nextView);
          void navigate({ pathname, search: search.toString() });
        }}
        value={view}
      >
        <div className="overflow-x-auto pb-1">
          <TabsList>
            {options.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>
      {children}
    </div>
  );
};

export const PerformanceCompatibilityPage = () => <PerformancePage />;

export const ExploreCompatibilityPage = () => {
  const { search } = useLocation();
  const logs = routeView(search) === 'logs';
  return (
    <WorkspaceTabs
      defaultView="compare"
      options={[
        { label: 'Compare', value: 'compare' },
        { label: 'Contextual logs', value: 'logs' },
      ]}
      pathname="/explore"
    >
      {logs ? <LogsPage /> : <QueryPage />}
    </WorkspaceTabs>
  );
};

export const SystemCompatibilityPage = () => {
  const { search } = useLocation();
  const definitions = routeView(search) === 'definitions';
  return (
    <WorkspaceTabs
      defaultView="collection"
      options={[
        { label: 'Collection health', value: 'collection' },
        { label: 'Metric definitions', value: 'definitions' },
      ]}
      pathname="/system"
    >
      {definitions ? <CatalogPage /> : <HealthPage />}
    </WorkspaceTabs>
  );
};

export const UsageCompatibilityPage = () => <UsagePage />;

export const LegacyRouteRedirect = ({ pathname, view }: { pathname: string; view?: string }): ReactNode => {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  if (view) search.set('view', view);

  return <Navigate replace to={{ pathname, search: search.toString() }} />;
};
